import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  Button,
  Alert,
  Divider,
  IconButton,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff, LockReset } from '@mui/icons-material';
import CopyrightIcon from '@mui/icons-material/Copyright';
import { updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';

const PasswordChange = () => {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const { isAuthenticated, userId, logout, token } = useAuth();

  useEffect(() => {
    if (isAuthenticated && tokenIsExpired(token)) {
      logout();
    }
  }, [isAuthenticated, token, logout]);

  const validatePassword = () => {
    const trimmedOld = oldPassword.trim();
    const trimmedNew = newPassword.trim();
    const trimmedConfirm = confirmPassword.trim();

    if (!trimmedOld) {
      setErrorMessage('Current password is required.');
      return false;
    }
    if (trimmedNew.length < 8) {
      setErrorMessage('Password must be at least 8 characters long.');
      return false;
    }
    if (!/[A-Z]/.test(trimmedNew)) {
      setErrorMessage('Password must contain at least one uppercase letter.');
      return false;
    }
    if (!/[a-z]/.test(trimmedNew)) {
      setErrorMessage('Password must contain at least one lowercase letter.');
      return false;
    }
    if (!/\d/.test(trimmedNew)) {
      setErrorMessage('Password must contain at least one digit.');
      return false;
    }
    if (trimmedNew !== trimmedConfirm) {
      setErrorMessage('Passwords do not match.');
      return false;
    }

    setErrorMessage('');
    return true;
  };

  const handleChangePassword = async () => {
    if (!validatePassword()) return;

    setLoading(true);

    const payload = {
      CurrentPassword: oldPassword.trim(),
      NewPassword: newPassword.trim(),
      ConfirmPassword: confirmPassword.trim(),
      UserId: userId,
    };

    try {
      const response = await updateData('UserTokenApi', `${userId}`, payload, token);
      setSuccessMessage(response.message || 'Password changed successfully.');
      setErrorMessage('');
    } catch (error) {
      const msg = error?.response?.data?.message || 'Failed to change password. Please try again.';
      setSuccessMessage('');
      setErrorMessage(msg);
      console.error('Password change error:', error);
    } finally {
      setLoading(false);
    }
  };

  const passwordFieldProps = {
    type: showPassword ? 'text' : 'password',
    InputProps: {
      endAdornment: (
        <InputAdornment position="end">
          <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
            {showPassword ? <VisibilityOff /> : <Visibility />}
          </IconButton>
        </InputAdornment>
      ),
    },
  };

  return (
    <Box
      sx={{
        maxWidth: 500,
        mx: 'auto',
        mt: 8,
        p: 4,
        borderRadius: 3,
        boxShadow: 4,
        backgroundColor: '#fff',
      }}
    >
      <Typography
        variant="h4"
        align="center"
        gutterBottom
        sx={{
          fontWeight: 800,
          color: 'transparent',
          backgroundImage: 'linear-gradient(90deg, #1976d2, #00c6ff)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          textTransform: 'uppercase',
          letterSpacing: '1.5px',
          fontFamily: 'Poppins, sans-serif',
          fontSize: '21px',
        }}
      >
        Change Password
      </Typography>

      <Divider sx={{ mb: 3 }} />

      {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}
      {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}

      <TextField
        fullWidth
        label="Old Password"
        variant="outlined"
        margin="normal"
        type="password"
        value={oldPassword}
        onChange={(e) => setOldPassword(e.target.value)}
      />

      <TextField
        fullWidth
        label="New Password"
        variant="outlined"
        margin="normal"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        {...passwordFieldProps}
      />

      <TextField
        fullWidth
        label="Confirm New Password"
        variant="outlined"
        margin="normal"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        {...passwordFieldProps}
      />

      <Button
        variant="contained"
        color="primary"
        fullWidth
        sx={{
          mt: 3,
          fontWeight: 'bold',
          py: 1.3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
        }}
        disabled={loading}
        onClick={handleChangePassword}
      >
        {loading ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          <>
            <LockReset fontSize="small" />
            UPDATE PASSWORD
          </>
        )}
      </Button>

      <Box sx={{ mt: 4, textAlign: 'center', color: 'gray' }}>
        <CopyrightIcon fontSize="small" />
        <Typography
          component="span"
          sx={{ ml: 1, fontFamily: 'cursive', fontWeight: 500, fontStyle: 'italic' }}
        >
          EFFTABS SOLUTIONS LLP
        </Typography>
      </Box>
    </Box>
  );
};

export default PasswordChange;
